import React, { useEffect, useMemo, useState } from 'react';
import {
  PartnerContactInformationFlatListRequest,
  useFetchPartnerContactInformationFlatQuery,
} from '../partnercontactinformationflat-api';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { ClearOutlined, DownloadOutlined, HomeOutlined } from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { PartnerContactInformationFlatViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import * as XLSX from 'xlsx';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';

const useQuery = () => new URLSearchParams(useLocation().search);

const COLUMN_MAP: Record<string, string> = {
  type: 'Type',
  partnerName: 'PartnerName',
  partnerId: 'PartnerId',
  identifierType: 'IdentifierType',
  identifierValue: 'IdentifierValue',
  partnerVan: 'PartnerVan',
  contactType: 'ContactType',
  contactName: 'ContactName',
  email: 'Email',
  phone: 'Phone',
  note: 'Note',
  primary: 'Primary',
};

const MISSING_SUPPORT_CONTACT_TYPE = 'Support';

const PARTNER_ONLY_COLUMN_KEYS = new Set([
  'type',
  'partnerName',
  'partnerId',
  'identifierType',
  'identifierValue',
  'partnerVan',
]);

export default function PartnerContactInformationFlatScreen() {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const [downLoading, setDownLoading] = useState(false);

  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const backendColumnName = COLUMN_MAP[columnKey] || columnKey;
    if (query.get('sortColumn') === backendColumnName) {
      return query.get('sortDirection') === 'DESC' ? 'descend' : 'ascend';
    }
    return null;
  };

  const tableConfig: TableState = {
    columns: [
      {
        title: 'Type',
        dataIndex: '',
        key: 'type',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.type || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('type'),
        fixed: 'left',
      },
      {
        title: 'Partner Name',
        dataIndex: '',
        key: 'partnerName',
        render: (item: PartnerContactInformationFlatViewModel) => (
          <Link to={getLink('PartnershipInformationEdit', { tenantId: params.tenantId, Id: item.partnerInformationId })}>
            {item.partnerName || ''}
          </Link>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('partnerName'),
      },
      {
        title: 'Partner Id',
        dataIndex: '',
        key: 'partnerId',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.partnerId || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('partnerId'),
      },
      {
        title: 'Identifier Type',
        dataIndex: '',
        key: 'identifierType',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.identifierType || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('identifierType'),
      },
      {
        title: 'Identifier Value',
        dataIndex: '',
        key: 'identifierValue',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.identifierValue || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('identifierValue'),
      },
      {
        title: 'Partner/VAN',
        dataIndex: '',
        key: 'partnerVan',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.partnerVan || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('partnerVan'),
      },
      {
        title: 'Contact Type',
        dataIndex: '',
        key: 'contactType',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.contactType || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('contactType'),
      },
      {
        title: 'Contact Name',
        dataIndex: '',
        key: 'contactName',
        render: (item: PartnerContactInformationFlatViewModel) => <span>{item.contactName || ''}</span>,
        sorter: true,
        sortOrder: getSortOrderForColumn('contactName'),
      },
      {
        title: 'Email',
        dataIndex: '',
        key: 'email',
        render: (item: PartnerContactInformationFlatViewModel) =>
          item.email ? <a href={`mailto:${item.email}`}>{item.email}</a> : <span />,
        sorter: true,
        sortOrder: getSortOrderForColumn('email'),
      },
      {
        title: 'Phone',
        dataIndex: '',
        key: 'phone',
        render: (item: PartnerContactInformationFlatViewModel) =>
          item.phone ? <a href={`tel:${item.phone}`}>{item.phone}</a> : <span />,
        sorter: true,
        sortOrder: getSortOrderForColumn('phone'),
      },
      {
        title: 'Note',
        dataIndex: '',
        key: 'note',
        render: (item: PartnerContactInformationFlatViewModel) => (
          <Tooltip title={item.note || ''}>
            <span>{item.note || ''}</span>
          </Tooltip>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('note'),
      },
      {
        title: 'Primary',
        dataIndex: '',
        key: 'primary',
        render: (item: PartnerContactInformationFlatViewModel) =>
          item.primary ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
        sorter: true,
        sortOrder: getSortOrderForColumn('primary'),
      },
    ],
  };

  const parseFilters = () => {
    const filterStr = query.get('q') || '';
    const result: Record<string, string> = {};
    Object.keys(COLUMN_MAP).forEach((key) => {
      const match = filterStr.match(new RegExp(`${key}:\\*([^*]*)\\*`));
      if (match?.[1]) result[key] = match[1];
    });
    return result;
  };

  const buildFilterJson = (filters: Record<string, string>) => {
    const filterObj: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) filterObj[COLUMN_MAP[key] || key] = value;
    });
    return Object.keys(filterObj).length > 0 ? JSON.stringify(filterObj) : null;
  };

  const getCurrentState = (): PartnerContactInformationFlatListRequest => {
    const filters = parseFilters();
    const searchTextValue = query.get('searchText');
    const missingContactType = query.get('missingContactType');
    return {
      current: Number(query.get('p')) || DefaultPage,
      pageSize: Number(query.get('size')) || DefaultPageSize,
      filter: query.get('q') || '',
      sortBy: query.get('sortBy'),
      sortColumn: query.get('sortColumn') || 'PartnerName',
      sortDirection: query.get('sortDirection') || 'ASC',
      searchText:
        searchTextValue && searchTextValue.trim() && searchTextValue !== 'null'
          ? searchTextValue
          : undefined,
      filterJson: buildFilterJson(filters),
      missingContactType: missingContactType || undefined,
    };
  };

  const currentPagination = useMemo(() => getCurrentState(), [location.search, params.tenantId]);
  const showMissingSupportContact =
    currentPagination.missingContactType === MISSING_SUPPORT_CONTACT_TYPE;

  const visibleColumns = useMemo(
    () =>
      showMissingSupportContact
        ? tableConfig.columns.filter((column) => PARTNER_ONLY_COLUMN_KEYS.has(column.key as string))
        : tableConfig.columns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showMissingSupportContact, location.search]
  );

  const {
    currentData: pagedData = {},
    isFetching,
    error,
  }: {
    currentData?: PaginatedResult<PartnerContactInformationFlatViewModel>;
    isFetching?: boolean;
    error?: any;
  } = useFetchPartnerContactInformationFlatQuery(currentPagination, {
    refetchOnMountOrArgChange: true,
  });

  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [partnerNameFilter, setPartnerNameFilter] = useState('');
  const [partnerIdFilter, setPartnerIdFilter] = useState('');
  const [contactTypeFilter, setContactTypeFilter] = useState('');
  const [primaryFilter, setPrimaryFilter] = useState('');

  useEffect(() => {
    const filters = parseFilters();
    const searchTextParam = query.get('searchText');
    setSearchText(!searchTextParam || searchTextParam === 'null' ? '' : searchTextParam);
    setTypeFilter(filters.type || '');
    setPartnerNameFilter(filters.partnerName || '');
    setPartnerIdFilter(filters.partnerId || '');
    setContactTypeFilter(filters.contactType || '');
    setPrimaryFilter(filters.primary || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const navigateWithState = (updates: Record<string, string | number | undefined>) => {
    const queryParams: Record<string, string> = {};
    query.forEach((val, key) => {
      queryParams[key] = val;
    });
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '') delete queryParams[key];
      else queryParams[key] = String(value);
    });
    navigate(getLink('PartnerContactInformationFlatList', { ...params }, queryParams));
  };

  const buildFilterQuery = (overrides: Record<string, string> = {}) => {
    const current = parseFilters();
    const merged = { ...current, ...overrides };
    const parts: string[] = [];
    Object.entries(merged).forEach(([key, value]) => {
      if (value) parts.push(`${key}:*${value}*`);
    });
    return parts.join(' AND ');
  };

  const handleFilterChange = (key: string, value?: string | null) => {
    navigateWithState({
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: buildFilterQuery({ [key]: value || '' }),
      searchText: searchText || undefined,
      missingContactType: currentPagination.missingContactType || undefined,
      sortColumn: currentPagination.sortColumn || undefined,
      sortDirection: currentPagination.sortDirection || undefined,
    });
  };

  const handleSearchTextChange = (value: string) => {
    navigateWithState({
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: buildFilterQuery(),
      searchText: value.trim() || undefined,
      missingContactType: currentPagination.missingContactType || undefined,
      sortColumn: currentPagination.sortColumn || undefined,
      sortDirection: currentPagination.sortDirection || undefined,
    });
  };

  const handleClearAllFilters = () => {
    navigateWithState({
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: '',
      searchText: undefined,
      missingContactType: undefined,
      sortColumn: currentPagination.sortColumn || undefined,
      sortDirection: currentPagination.sortDirection || undefined,
    });
  };

  const handleMissingSupportContactChange = (checked: boolean) => {
    navigateWithState({
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: checked ? '' : currentPagination.filter || '',
      searchText: checked ? undefined : searchText || undefined,
      missingContactType: checked ? MISSING_SUPPORT_CONTACT_TYPE : undefined,
      sortColumn: currentPagination.sortColumn || undefined,
      sortDirection: currentPagination.sortDirection || undefined,
    });
  };

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<PartnerContactInformationFlatViewModel>
      | SorterResult<PartnerContactInformationFlatViewModel>[],
    extra: TableCurrentDataSource<PartnerContactInformationFlatViewModel>
  ) => {
    const prevSortColumn = currentPagination.sortColumn || 'PartnerName';
    const prevSortDirection = currentPagination.sortDirection || 'ASC';
    const pageSize = paginationConfig.pageSize || currentPagination.pageSize;

    // A sort action resets to the first page with the new sort; any other action
    // (pagination / page-size change) keeps the current sort and honors the page.
    if (extra.action === 'sort') {
      const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
      const { columnKey, order } = sorterResult || {};
      const newSortColumn = order
        ? COLUMN_MAP[columnKey?.toString() || ''] || columnKey?.toString() || prevSortColumn
        : 'PartnerName';
      const newSortDirection = order ? (order === 'ascend' ? 'ASC' : 'DESC') : 'ASC';

      navigateWithState({
        sortColumn: newSortColumn,
        sortDirection: newSortDirection,
        p: DefaultPage,
        size: pageSize,
        q: currentPagination.filter || '',
        searchText: searchText || undefined,
        missingContactType: currentPagination.missingContactType || undefined,
      });
      return;
    }

    const newPage =
      pageSize !== currentPagination.pageSize ? DefaultPage : paginationConfig.current || 1;

    navigateWithState({
      sortColumn: prevSortColumn,
      sortDirection: prevSortDirection,
      p: newPage,
      size: pageSize,
      q: currentPagination.filter || '',
      searchText: searchText || undefined,
      missingContactType: currentPagination.missingContactType || undefined,
    });
  };

  const getUniqueValues = (key: keyof PartnerContactInformationFlatViewModel): string[] => {
    if (!pagedData.results) return [];
    return pagedData.results
      .map((item) => {
        const value = item[key];
        if (key === 'primary') return value ? 'Yes' : 'No';
        return value;
      })
      .filter((value): value is string => value !== null && value !== undefined && value !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
  };

  const onExport = async () => {
    try {
      setDownLoading(true);
      const currentState = getCurrentState();
      const totalCount = pagedData?.totalCount || 0;
      if (totalCount === 0) {
        message.warning('No data available to export');
        return;
      }

      const urlParams = new URLSearchParams();
      urlParams.append('pageNumber', '1');
      urlParams.append('pageSize', totalCount.toString());
      if (currentState.sortColumn) urlParams.append('sortColumn', currentState.sortColumn);
      if (currentState.sortDirection) urlParams.append('sortDirection', currentState.sortDirection);
      if (currentState.searchText) urlParams.append('searchText', currentState.searchText);
      if (currentState.filterJson) urlParams.append('filterJson', currentState.filterJson);
      if (currentState.missingContactType) {
        urlParams.append('missingContactType', currentState.missingContactType);
      }

      const baseUrl = process.env.REACT_APP_BASE_API_URL || '';
      const response = await fetch(
        `${baseUrl}/partnershipInformationContacts/partner-contact-flat?${urlParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data: PaginatedResult<PartnerContactInformationFlatViewModel> = await response.json();
      const exportRows =
        data.results?.map((row) => {
          const base = {
            Type: row.type,
            'Partner Name': row.partnerName,
            'Partner Id': row.partnerId,
            'Identifier Type': row.identifierType,
            'Identifier Value': row.identifierValue,
            'Partner/VAN': row.partnerVan,
          };
          if (showMissingSupportContact) return base;
          return {
            ...base,
            'Contact Type': row.contactType,
            'Contact Name': row.contactName,
            Email: row.email,
            Phone: row.phone,
            Note: row.note,
            Primary: row.primary ? 'Yes' : 'No',
          };
        }) || [];

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Partner Contact Information');
      XLSX.writeFile(
        workbook,
        `PartnerContactInformation_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      message.success(`Successfully exported ${exportRows.length} records`);
    } catch (exportError) {
      console.error('Export error:', exportError);
      message.error('Failed to export data. Please try again.');
    } finally {
      setDownLoading(false);
    }
  };

  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  return (
    <div>
      <PageHeader
        title={<Title level={4} style={{ marginBottom: 0, display: 'inline-block' }}>Partner Contact Information</Title>}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
                <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={getLink('cob', { tenantId: params.tenantId })}>Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Partner Contact Information</Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={
          <Button htmlType="button" onClick={onExport} loading={downLoading} icon={<DownloadOutlined />}>
            Download as Excel
          </Button>
        }
      >
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Checkbox
                checked={showMissingSupportContact}
                onChange={(e) => handleMissingSupportContactChange(e.target.checked)}
              >
                Show partners with no Support contact
              </Checkbox>
            </Col>
          </Row>
          {showMissingSupportContact && (
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message="Showing unique partners that do not have a Support contact. Uncheck the box or use Clear All Filters to return to the full contact list."
            />
          )}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span>Search</span>
                <Input
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => handleSearchTextChange(e.target.value)}
                  allowClear
                />
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span>Type</span>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  allowClear
                  placeholder="Filter by type"
                  value={typeFilter || undefined}
                  onChange={(value) => handleFilterChange('type', value)}
                  options={getUniqueValues('type').map((item) => ({ label: item, value: item }))}
                  filterOption={filterOption}
                  loading={isFetching}
                />
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span>Partner Name</span>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  allowClear
                  placeholder="Filter by partner name"
                  value={partnerNameFilter || undefined}
                  onChange={(value) => handleFilterChange('partnerName', value)}
                  options={getUniqueValues('partnerName').map((item) => ({ label: item, value: item }))}
                  filterOption={filterOption}
                  loading={isFetching}
                />
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span>Partner Id</span>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  allowClear
                  placeholder="Filter by partner id"
                  value={partnerIdFilter || undefined}
                  onChange={(value) => handleFilterChange('partnerId', value)}
                  options={getUniqueValues('partnerId').map((item) => ({ label: item, value: item }))}
                  filterOption={filterOption}
                  loading={isFetching}
                />
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span>Contact Type</span>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  allowClear
                  disabled={showMissingSupportContact}
                  placeholder="Filter by contact type"
                  value={contactTypeFilter || undefined}
                  onChange={(value) => handleFilterChange('contactType', value)}
                  options={getUniqueValues('contactType').map((item) => ({ label: item, value: item }))}
                  filterOption={filterOption}
                  loading={isFetching}
                />
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span>Primary</span>
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  disabled={showMissingSupportContact}
                  placeholder="Filter by primary"
                  value={primaryFilter || undefined}
                  onChange={(value) => handleFilterChange('primary', value)}
                  options={[
                    { label: 'Yes', value: 'Yes' },
                    { label: 'No', value: 'No' },
                  ]}
                />
              </Space>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button htmlType="button" onClick={handleClearAllFilters} icon={<ClearOutlined />} danger>
                Clear All Filters
              </Button>
            </Col>
          </Row>
        </Card>

        <Card>
          {error?.status && <Alert message="Error" description={error.data} type="error" showIcon />}
          <Table
            key={`partner-contact-flat-${showMissingSupportContact ? 'missing-support' : 'all'}-${currentPagination.pageSize}`}
            scroll={{ x: true }}
            dataSource={pagedData?.results || []}
            onChange={handleTableChange}
            rowKey={(record) =>
              showMissingSupportContact
                ? `${record.type}-${record.partnerId}`
                : `${record.contactId}-${record.partnerInformationId}`
            }
            pagination={{
              current: currentPagination.current,
              pageSize: currentPagination.pageSize,
              total: pagedData?.totalCount || 0,
              showTotal: (total) => `Total ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100', '10000'],
              showSizeChanger: true,
              locale: { items_per_page: ' / Page' },
            }}
            columns={visibleColumns}
            loading={isFetching}
          />
        </Card>
      </PageHeader>
    </div>
  );
}
